import styled from 'styled-components';
import React, {FC} from 'react';

import {mediaBreakpointDown, palette, mediaBreakpointUp} from '../../../../lib/styleVariables';
import {PrimaryLink, SecondaryLink} from '../../../molecules/buttons';
import {NavigationCallToActionBase} from './navigationCallToActionBase';
import {NavigationCallToActionSectionTitle} from './navigationCallToActionSectionTitle';
import {StylableProps} from '../../../../lib/styleHelpers';
import {Handler} from '../../../../lib/typeHelpers';
import {paletteR4} from '../../../../lib/styleVariablesR4';

export interface DynamicNavigationCallToActionTwoButtonProps {
  onButtonClick?: Handler;
}

interface NavigationCallToActionTwoButtonProps extends StylableProps {
  primaryLabel: string;
  primaryHref: string;
  primaryHover?: boolean;
  secondaryLabel?: string;
  secondaryHref?: string;
  title: React.ReactNode;
  color?: 'gray' | 'black' | 'blue';
}

export const getPrimaryButtonStyles = (ctaColor?: string) => {
  switch (ctaColor) {
    case 'gray':
      return '';
    case 'black':
      return blackCtaPrimaryButtonStyle;
    default:
      return blueCtaPrimaryButtonStyle;
  }
};

export const getSecondaryButtonStyles = (ctaColor?: string) => {
  switch (ctaColor) {
    case 'gray':
      return '';
    case 'black':
      return blackCtaSecondaryButtonStyle;
    default:
      return blueCtaSecondaryButtonStyle;
  }
};

const leftStyle = `
  flex: 1;
`;

const StyledButtonsDiv = styled.div`
  display: flex;
  justify-content: flex-end;

  ${mediaBreakpointDown('mobile')} {
    flex-direction: column;
  }
`;

const buttonStyle = `
 transition-property: background, color, box-shadow;
 padding-top: 15px;
 padding-bottom: 15px;
 line-height: 18px;
  ${mediaBreakpointDown('mobile')} {
    &:not(:last-child) {
      margin-bottom: 16px;
    }
  }

  ${mediaBreakpointUp('tablet')} {
    &:not(:last-child) {
      margin-right: 16px;
    }
  }
`;

const blackCtaPrimaryButtonStyle = `
  background: ${palette.white.highlight};
  color: ${palette.black.base};
  &:hover {
    background: ${paletteR4.blue.lowlight};
    color: ${palette.white.highlight};
  }
`;

const blackCtaSecondaryButtonStyle = `
  color: ${palette.white.highlight};
  box-shadow: inset 0 0 0 3px ${palette.white.highlight};

  &:hover {
    box-shadow: inset 0 0 0 3px ${paletteR4.blue.lowlight};
    background: ${paletteR4.blue.lowlight};
    color: ${palette.white.highlight};
  }
`;

const blueCtaPrimaryButtonStyle = `
  background: ${palette.white.highlight};
  color: ${palette.black.base};

  &:hover {
    background: ${palette.black.base};
    color: ${palette.white.highlight};
  }
`;

const blueCtaSecondaryButtonStyle = `
  color: ${palette.white.highlight};
  box-shadow: inset 0 0 0 3px ${palette.white.highlight};

  &:hover {
    background: ${palette.black.base};
    color: ${palette.white.highlight};
    box-shadow: inset 0 0 0 3px ${palette.black.base};
  }

  [data-theme='dark'] & {
    background: ${palette.white.highlight};
    color: ${palette.black.highlight};
    transition-property: background, color, box-shadow;
    &:hover {
      background: ${palette.black.base};
      color: ${palette.white.highlight};
    }
  }
`;

const StyledPrimaryLink = styled(PrimaryLink)<{$css?: string}>`
  ${buttonStyle}
  ${props => props.$css}
  box-shadow: none;
`;
const StyledSecondaryLink = styled(SecondaryLink)<{$css?: string}>`
  ${buttonStyle}
  ${props => props.$css}
`;

export const NavigationCallToActionTwoButton: FC<NavigationCallToActionTwoButtonProps &
  DynamicNavigationCallToActionTwoButtonProps> = props => {
    const {color, primaryHref, primaryLabel, secondaryHref, secondaryLabel, title, onButtonClick} = props;
    const hasPrimaryButton = primaryHref && primaryLabel;
    const hasSecondaryButton = secondaryHref && secondaryLabel;
    const onClickEvent = (e: any) => {
      if (onButtonClick) {
        e.preventDefault();
        onButtonClick();
      }
    };

    return (
      <NavigationCallToActionBase
        color={color}
        leftCss={leftStyle}
        renderLeft={() => (
          <NavigationCallToActionSectionTitle color={color}>{title}</NavigationCallToActionSectionTitle>
        )}
        renderRight={() => (
          <StyledButtonsDiv>
            {hasPrimaryButton && (
              <StyledPrimaryLink
                href={primaryHref}
                $css={getPrimaryButtonStyles(color)}
                // @ts-ignore
                onClick={onClickEvent}
              >
                {primaryLabel}
              </StyledPrimaryLink>
            )}
            {hasSecondaryButton && (
              <StyledSecondaryLink
                href={secondaryHref}
                $css={getSecondaryButtonStyles(color)}
                // @ts-ignore
                onClick={onClickEvent}
              >
                {secondaryLabel}
              </StyledSecondaryLink>
            )}
          </StyledButtonsDiv>
        )}
      />
    );
  };
